import type { ActiveSheet } from '@store'
import { useUiPresentationStore } from '@store'
import type { ReactNode } from 'react'
import { useEffect } from 'react'

type SheetHostProps = {
  title: string
  children: ReactNode
  heightClassName?: string
}

const SHEET_TITLES: Record<Exclude<ActiveSheet, null>, string> = {
  modules: 'Módulos',
  options: 'Opciones',
  textures: 'Tejidos y color',
  more: 'Más acciones',
}

/**
 * Bottom sheet presentacional. Solo lee/escribe uiPresentationStore.
 * El área de contenido es quien hace scroll (overflow-y-auto + min-h-0).
 * Queda por encima del ModeBar (pb-14) para no tapar la navegación.
 */
export function SheetHost({
  title,
  children,
  heightClassName = 'h-[min(70dvh,640px)]',
}: SheetHostProps) {
  const closeSheet = useUiPresentationStore((s) => s.closeSheet)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSheet()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [closeSheet])

  return (
    <div className='fixed inset-0 z-[80] flex flex-col justify-end pb-14'>
      <button
        type='button'
        aria-label='Cerrar panel'
        className='absolute inset-0 bg-black/35'
        onClick={closeSheet}
      />
      <div
        role='dialog'
        aria-modal='true'
        aria-label={title}
        className={`relative z-[1] flex ${heightClassName} w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-[0_-8px_32px_rgba(0,0,0,0.18)]`}
      >
        <div className='relative flex shrink-0 items-center border-b border-[#E8DFC8] px-4 pb-3 pt-3'>
          <h2 className='flex-1 text-base font-semibold text-C/Color3'>{title}</h2>
          <button
            type='button'
            onClick={closeSheet}
            className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-C/Color3 transition-colors hover:bg-[#F5F0E4]'
            aria-label='Cerrar'
          >
            <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
        <div
          className='min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain'
          style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

export function ActiveSheetHost({
  children,
}: {
  children: (sheet: Exclude<ActiveSheet, null>) => ReactNode
}) {
  const activeSheet = useUiPresentationStore((s) => s.activeSheet)
  if (!activeSheet) return null

  const heightBySheet: Record<Exclude<ActiveSheet, null>, string> = {
    modules: 'h-[min(50dvh,460px)]',
    options: 'h-[min(52dvh,500px)]',
    textures: 'h-[min(55dvh,520px)]',
    more: 'h-[min(45dvh,400px)]',
  }

  return (
    <SheetHost title={SHEET_TITLES[activeSheet]} heightClassName={heightBySheet[activeSheet]}>
      {children(activeSheet)}
    </SheetHost>
  )
}
